import { useEffect, useState } from 'react'
import { useEquipo, useEquipoRefresh } from './EquipoContext'
import { useLang } from '../i18n/LangProvider'
import { Miembro } from './EquipoContext'
import { t, Lang } from './i18n'
import { FichaOverlay } from '../asistencia/FichaOverlay'
import { FichaOverlayF11 } from '../asistencia/f11/FichaOverlayF11'
import { formatDate, localeFor } from '../utils/format'
import styles from './PlantillaTab.module.css'
import { useAsistenciaApi } from '../asistencia/api'
import { guardarFichero } from '../asistencia/descargar'
import { useAuth } from '../auth/useAuth'
import { tieneRol } from '../auth/roles'

// Issue #80 (club, 2026-10-22): los delegados "siguen apareciendo en la lista" aunque no cuenten en
// asistencia ni minutaje — sección propia al final.
const GRUPOS: { key: Miembro['grupo']; labelKey: 'conFicha' | 'sinFicha' | 'entrenadores' | 'delegados' }[] = [
  { key: 'con_ficha', labelKey: 'conFicha' },
  { key: 'sin_ficha', labelKey: 'sinFicha' },
  { key: 'entrenador', labelKey: 'entrenadores' },
  { key: 'delegado', labelKey: 'delegados' },
]

function iniciales(nombre: string): string {
  return nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

export function PlantillaTab({ lang: langProp }: { lang?: Lang }) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const equipo = useEquipo()
  const refresh = useEquipoRefresh()
  const locale = localeFor(lang)
  // Feedback de navegación del cliente, 2026-10-02: "plantilla solo lista jugadores y no hace
  // nada, enlazar con algo" — el nombre de cada jugador ahora abre la misma ficha de estadísticas
  // ya disponible en Asistencia (F11 o Eskola/F7 según la categoría del equipo).
  const [fichaMiembroId, setFichaMiembroId] = useState<string | null>(null)
  const esF11 = equipo.categoria === 'f11'

  const api = useAsistenciaApi()
  const { user } = useAuth()

  // Issue #89 (club, 2026-10-26): "en el apartado de plantilla, el administrador debe poder cambiar el
  // orden de las listas que aparecen, utilizando un sistema de arrastrar y soltar". Se reordena dentro
  // de cada sección (con/sin ficha, entrenadores, delegados) y se guarda en el mismo `orden` que usa la
  // tabla de Asistencias, así que el orden es el mismo en las dos pestañas. Solo admin y temporada abierta.
  const puedeOrdenar = tieneRol(user, 'admin') && equipo.temporada.estado !== 'cerrada'
  const [dragId, setDragId] = useState<string | null>(null)
  const [ordenLocal, setOrdenLocal] = useState<Record<string, number> | null>(null)

  async function soltar(grupo: Miembro['grupo'], targetId: string) {
    const origen = dragId
    setDragId(null)
    if (!origen || origen === targetId) return
    const lista = equipo.miembros.filter((m) => m.grupo === grupo).sort((a, b) => ordenDe(a) - ordenDe(b))
    const desde = lista.findIndex((m) => m.id === origen)
    const hasta = lista.findIndex((m) => m.id === targetId)
    if (desde === -1 || hasta === -1) return
    const reordenada = [...lista]
    const [movido] = reordenada.splice(desde, 1)
    reordenada.splice(hasta, 0, movido)
    // Se ve al momento; si falla, se vuelve al orden guardado.
    setOrdenLocal((prev) => ({ ...prev, ...Object.fromEntries(reordenada.map((m, i) => [m.id, i])) }))
    try {
      await Promise.all(reordenada.map((m, i) => api.patch(`/equipos/${equipo.id}/miembros/${m.id}`, { orden: i })))
    } catch (err) {
      setOrdenLocal(null)
      throw err
    }
    refresh()
  }

  // El orden provisional se mantiene hasta que llega la plantilla recargada (sin parpadeo).
  useEffect(() => {
    setOrdenLocal(null)
  }, [equipo.miembros])

  function ordenDe(m: Miembro): number {
    return ordenLocal?.[m.id] ?? m.orden
  }

  // Issue #91 (club, 2026-10-30): la plantilla viene del equipo cargado al entrar; si se eliminaba un
  // jugador (o se añadía o cambiaba de rol) desde Asistencias, Plantilla no se enteraba hasta recargar.
  // Al abrir la pestaña se vuelve a pedir.
  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      {/* Issue #76: Excel de la plantilla (jugadores, entrenadores y delegados). */}
      <div className={styles.acciones}>
        <button onClick={() => guardarFichero(api.downloadFile(`/equipos/${equipo.id}/plantilla/exportar?lang=${lang}`))}>
          {t('excelEquipo', lang)}
        </button>
      </div>
      {GRUPOS.map(({ key, labelKey }) => {
        const miembros = equipo.miembros
          .filter((m) => m.grupo === key)
          .sort((a, b) => ordenDe(a) - ordenDe(b))

        if (miembros.length === 0) return null

        return (
          <section key={key}>
            <h3>{t(labelKey, lang)}</h3>
            <ul className={styles.list}>
              {miembros.map((miembro) => (
                <li
                  key={miembro.id}
                  className={styles.item}
                  {...(puedeOrdenar && {
                    draggable: true,
                    title: t('arrastrarParaOrdenar', lang),
                    'data-arrastrando': dragId === miembro.id ? 'true' : undefined,
                    onDragStart: () => setDragId(miembro.id),
                    onDragEnd: () => setDragId(null),
                    onDragOver: (e: React.DragEvent) => e.preventDefault(),
                    onDrop: () => soltar(key, miembro.id),
                  })}
                >
                  {puedeOrdenar && (
                    <span aria-hidden className={styles.asa}>
                      ⠿
                    </span>
                  )}
                  <span className={styles.avatar}>
                    {miembro.persona.foto_url ? (
                      <img src={miembro.persona.foto_url} alt="" width={40} height={40} />
                    ) : (
                      <span aria-hidden>{iniciales(miembro.persona.nombre)}</span>
                    )}
                  </span>
                  {/* fix-mobile-responsive: nombre arriba y datos debajo, en vez de todo en una fila que en
                      móvil partía nombre y fecha en columnas estrechas. */}
                  <span className={styles.info}>
                    <span>
                      <button className={styles.nombre} onClick={() => setFichaMiembroId(miembro.id)}>
                        <strong>{miembro.persona.nombre}</strong>
                      </button>
                      {miembro.persona.alias && <span> ({miembro.persona.alias})</span>}
                      {miembro.rol_entrenador && <span> — {miembro.rol_entrenador}</span>}
                    </span>
                    <span className={styles.meta}>
                      {t('fechaIncorporacion', lang)}: {formatDate(miembro.fecha_incorporacion, locale)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )
      })}

      {fichaMiembroId && esF11 && (
        <FichaOverlayF11
          equipoId={equipo.id}
          miembroId={fichaMiembroId}
          lang={lang}
          onClose={() => setFichaMiembroId(null)}
          onChanged={refresh}
        />
      )}
      {fichaMiembroId && !esF11 && (
        <FichaOverlay
          equipoId={equipo.id}
          miembroId={fichaMiembroId}
          lang={lang}
          onClose={() => setFichaMiembroId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  )
}
